// Use `m` (not `motion`) because the app wraps the tree in
// <LazyMotion features={domAnimation} strict> for tree-shaking —
// strict mode rejects full `motion.*` components. The visual and
// animation behaviour is identical, just deferred until the
// LazyMotion feature bundle loads.
import { m } from "framer-motion";

/**
 * FTL Monolith Icon — animated React component
 *
 * Dependencies:
 *   npm install motion
 *   (Tailwind assumed already configured)
 *
 * Usage:
 *   <FTLIcon size={320} />
 *   <FTLIcon size={128} interactive={false} />
 */
export default function FTLIcon({ size = 320, interactive = true }) {
  return (
    <m.div
      className="relative inline-block"
      style={{ width: size, height: size }}
      initial="rest"
      animate="rest"
      whileHover={interactive ? "hover" : "rest"}
    >
      {/* Ambient glow behind icon */}
      <m.div
        className="absolute inset-0 rounded-[22%] blur-2xl"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(0,255,255,0.45), rgba(0,100,255,0.15) 40%, transparent 70%)",
        }}
        animate={{
          scale: [1, 1.08, 1],
          opacity: [0.6, 0.9, 0.6],
        }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
      />

      <m.svg
        viewBox="0 0 300 300"
        className="relative w-full h-full drop-shadow-[0_0_30px_rgba(0,200,255,0.35)]"
        variants={{
          rest: { scale: 1, rotate: 0 },
          hover: { scale: 1.04, rotate: -1 },
        }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
      >
        <defs>
          {/* Main F gradient */}
          <linearGradient id="fGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#7dfaff" />
            <stop offset="45%" stopColor="#00c8ff" />
            <stop offset="100%" stopColor="#0044aa" />
          </linearGradient>

          {/* Background gradient */}
          <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0a1a3a" />
            <stop offset="60%" stopColor="#05081f" />
            <stop offset="100%" stopColor="#020208" />
          </linearGradient>

          {/* Animated sheen gradient (moves across F) */}
          <linearGradient id="sheen" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="45%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.55" />
            <stop offset="55%" stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
            <animate
              attributeName="x1"
              values="-100%;100%"
              dur="3s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="x2"
              values="0%;200%"
              dur="3s"
              repeatCount="indefinite"
            />
          </linearGradient>

          {/* Glow filter */}
          <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" />
          </filter>

          {/* Clip for F shape (used for sheen masking) */}
          <clipPath id="fClip">
            <path d="M 95 80 L 210 80 L 210 112 L 130 112 L 130 140 L 190 140 L 190 170 L 130 170 L 130 220 L 95 220 Z" />
          </clipPath>
        </defs>

        {/* Rounded-square background */}
        <rect
          x="20"
          y="20"
          width="260"
          height="260"
          rx="56"
          fill="url(#bgGrad)"
        />

        {/* Outer border */}
        <rect
          x="20"
          y="20"
          width="260"
          height="260"
          rx="56"
          fill="none"
          stroke="#00ffff"
          strokeWidth="1"
          strokeOpacity="0.35"
        />

        {/* Inner bevel */}
        <rect
          x="28"
          y="28"
          width="244"
          height="244"
          rx="50"
          fill="none"
          stroke="#00ffff"
          strokeWidth="0.5"
          strokeOpacity="0.2"
        />

        {/* Pulsing halo behind F */}
        <m.ellipse
          cx="150"
          cy="150"
          rx="75"
          ry="90"
          fill="#00ffff"
          filter="url(#softGlow)"
          animate={{ opacity: [0.08, 0.18, 0.08] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* F shadow (depth) */}
        <path
          d="M 95 80 L 210 80 L 210 112 L 130 112 L 130 140 L 190 140 L 190 170 L 130 170 L 130 220 L 95 220 Z"
          fill="#000000"
          opacity="0.5"
          transform="translate(2, 4)"
        />

        {/* Glowing F base */}
        <path
          d="M 95 80 L 210 80 L 210 112 L 130 112 L 130 140 L 190 140 L 190 170 L 130 170 L 130 220 L 95 220 Z"
          fill="url(#fGrad)"
          filter="url(#softGlow)"
          opacity="0.9"
        />

        {/* Solid F */}
        <path
          d="M 95 80 L 210 80 L 210 112 L 130 112 L 130 140 L 190 140 L 190 170 L 130 170 L 130 220 L 95 220 Z"
          fill="url(#fGrad)"
        />

        {/* Top highlight edge */}
        <path
          d="M 95 80 L 210 80 L 210 86 L 101 86 L 101 220 L 95 220 Z"
          fill="white"
          opacity="0.35"
        />

        {/* Animated sheen sweep (clipped to F) */}
        <g clipPath="url(#fClip)">
          <rect x="0" y="0" width="300" height="300" fill="url(#sheen)" />
        </g>

        {/* Sparkle particles */}
        {[
          { cx: 70, cy: 60, d: 0 },
          { cx: 235, cy: 95, d: 0.7 },
          { cx: 245, cy: 210, d: 1.4 },
          { cx: 60, cy: 240, d: 2.1 },
          { cx: 220, cy: 50, d: 2.8 },
          { cx: 50, cy: 150, d: 3.5 },
        ].map((s, i) => (
          <m.g key={i}>
            <m.circle
              cx={s.cx}
              cy={s.cy}
              r="1.5"
              fill="#ffffff"
              animate={{
                opacity: [0, 1, 0],
                scale: [0.5, 1.8, 0.5],
              }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                delay: s.d,
                ease: "easeInOut",
              }}
            />
            <m.circle
              cx={s.cx}
              cy={s.cy}
              r="4"
              fill="#00ffff"
              filter="url(#softGlow)"
              animate={{
                opacity: [0, 0.6, 0],
                scale: [0.5, 1.5, 0.5],
              }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                delay: s.d,
                ease: "easeInOut",
              }}
            />
          </m.g>
        ))}

        {/* Corner tech accents */}
        <g stroke="#00ffff" strokeWidth="1.5" opacity="0.55" fill="none">
          <m.path
            d="M 40 55 L 40 40 L 55 40"
            animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <m.path
            d="M 260 55 L 260 40 L 245 40"
            animate={{ opacity: [0.8, 0.3, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <m.path
            d="M 40 245 L 40 260 L 55 260"
            animate={{ opacity: [0.8, 0.3, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <m.path
            d="M 260 245 L 260 260 L 245 260"
            animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </g>
      </m.svg>
    </m.div>
  );
}
